import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Icon, type IconName } from '@/components/Icon'
import { cx } from '@/lib/cx'

/** Two columns from the smallest phone up: one tile per row meant six screens
 *  of scrolling for six numbers four characters long. */
export function StatGrid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">{children}</div>
  )
}

const TONES = {
  default: 'bg-accent-soft text-accent',
  warn: 'bg-warning-soft text-warning',
  good: 'bg-success-soft text-success',
}

/** Label, value, then where the number goes if you follow it. Never a number
 *  alone, and never a tone carried by colour alone. */
export function StatTile({
  label,
  value,
  icon,
  tone = 'default',
  hint,
  to,
}: {
  label: string
  value: number | string
  icon: IconName
  tone?: keyof typeof TONES
  hint?: string
  to?: string
}) {
  const body = (
    <>
      <span className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-fg-muted">{label}</span>
        <span
          className={cx(
            'grid size-7 flex-none place-items-center rounded-md sm:size-8',
            TONES[tone],
          )}
        >
          <Icon name={icon} />
        </span>
      </span>
      <span className="text-3xl leading-tight font-bold tracking-tight tabular-nums sm:text-4xl">
        {value}
      </span>
      {(hint || to) && (
        <span
          className={cx(
            'flex items-center gap-1 text-xs',
            to ? 'font-medium text-accent' : 'text-fg-subtle',
          )}
        >
          {hint ?? 'View list'}
          {to && (
            <span
              className="transition-transform duration-150 ease-standard group-hover:translate-x-0.5"
              aria-hidden="true"
            >
              →
            </span>
          )}
        </span>
      )}
    </>
  )

  const shell =
    'grid content-start gap-1 rounded-lg border border-line-soft bg-surface px-4 py-3 text-fg no-underline sm:gap-2 sm:px-5 sm:py-4'

  // Hover moves, because movement is the affordance: it is the tile saying
  // there is a list behind the number. Plain tiles stay still.
  return to ? (
    <Link
      to={to}
      className={cx(
        shell,
        'group transition duration-150 ease-standard hover:-translate-y-0.5 hover:border-accent-line hover:shadow-md active:translate-y-0 active:shadow-none',
      )}
    >
      {body}
    </Link>
  ) : (
    <div className={shell}>{body}</div>
  )
}
